<template>
  <div style="padding: 10px 20px;">
    <div style="display: flex; align-items: center; justify-content: space-between">
      <div style="display: flex; align-items: center">
        <div style="width: 250px;">
          <OrgSelect v-model="selectedOrg" @orgName="handleOrgName"/>
        </div>
        <div style="margin-left: 30px; width: 300px">
          <v-text-field v-model="search" append-icon="mdi-magnify" label="Search" single-line hide-details />
        </div>
        <div style="margin-left: 30px" v-if="$can('getVehiclePresence')">
          <v-chip-group v-model="selectedFilter" multiple active-class="third--text" class="ma-4">
            <v-chip v-for="tag in filters" :key="tag">{{ tag.text }}</v-chip>
          </v-chip-group>
        </div>
      </div>
      <div>
        <v-icon style="margin-right: 10px" @click="updateWithOrg(selectedOrg)"> mdi-refresh </v-icon>
        <v-icon style="margin-right: 10px" @click="download">mdi-download</v-icon>
      </div>
    </div>  
  
    <v-data-table
      :headers="filteredHeaders"
      :items="filteredSet"
      :search="search"
      item-key="key"
      :loading="loading > 0"
      loading-text="Loading... Please wait"
      :items-per-page="-1"
    >

    <template v-slot:item.status="{ item }">
      <Status :status="item.status" />
    </template>

    <template v-slot:item.actions="{ item }">
      <DeviceMenu :device="item.device_id" :org="selectedOrg" :standAlone="true" />
    </template>
    
    </v-data-table>
  </div>
</template>

<script>
import { GetOrgVehicles, GetOrgVehiclePresence } from "@/services/API"
import { getCoor } from "@/utils"
import { Logger } from "aws-amplify"
import { locationsHelper } from '@/mixins/locations-helper.js'
import OrgSelect from "@/components/general/OrgSelect"
import DeviceMenu from "@/components/general/DeviceMenu.vue"
import Status from "@/components/general/VehicleStatus"
import moment from "moment"
import xlsx from 'xlsx'
import { Promise } from "core-js"

import { presence } from '@/mixins/presence'


const logger = new Logger("FLEET STATUS")

export default {
  mixins: [ locationsHelper, presence ],

  components: {
    OrgSelect,
    Status,
    DeviceMenu
  },

  data () {
    return {
      vehicles: [],
      presences: [],
      selectedOrg: false,
      orgName: '',
      selectedFilter: [],
      tableData: [],
      loadingTimeout: false, // we use this to clear any loading in the event of an error since some of the loading is non observable
      loading: 0,
      search: '', // search string for table
      headers: [
        {
          text: 'Status',
          value: 'status',
          align: 'left'
        },
        {
          text: 'Vehicle',
          value: 'vehicle',
          align: 'left'
        },
        {
          text: 'Last Seen',
          value: 'time',
          align: 'left'
        },
        {
          text: 'Location',
          value: 'location',
          align: 'left'
        },
        {
          text: 'Speed (kph)',
          value: 'speed',
          align: 'left'
        },
        {
          text: 'Mileage (km)',
          value: 'mileage',
          align: 'left'
        },
        {
          text: 'Ignition',
          value: 'ignition',
          align: 'left'
        },
        {
          text: 'Actions',
          value: 'actions',
          align: 'left'
        }
      ],
    }
  },

  computed: {
    // hide table headers the user should not see
    filteredHeaders () {
      return this.headers.filter(i => !this.hideHeader[i.value])
    },

    hideHeader () {
      return {
        status: !this.$can('getVehiclePresence')
      }
    },

    filteredSet () {
      if(!this.selectedFilter.length) {
        return this.tableData
      } else {
        const filters = this.selectedFilter.map(i => this.filters[i].val)

        return this.tableData.filter(i => filters.includes(i.status))
      }
    },

    filters () {
      return [
        { text: 'Unknown', val: 'UNKNOWN'},
        { text: 'Offline', val: 'OFFLINE'},
        { text: 'Park Mode', val: 'STATIONARY'},
        { text: 'Idling', val: 'IDLING'},
        { text: 'Driving', val: 'MOVING'}
      ]
    }
  },

  watch: {
    async selectedOrg (val) {
      if(val) {
        this.updateWithOrg(val)
      }
    }
  },
  
  mounted() {
    logger.debug("mounted", window)
  },

  methods: {
    handleOrgName(name) {
      this.orgName = name;
    },  

    getVehicles(orgId) {
      return new Promise(res => {
        GetOrgVehicles(orgId, true).then(result => {
          this.vehicles = result.reduce((vehiclesMap, vehicle) => {
            vehiclesMap[vehicle.id] = vehicle.vehicle_name
            return vehiclesMap
          }, {})

          this.$loadPresence(orgId, result)
          
          res(true)
        })
        .then(() => {
          
        })
        .catch(err => {
          logger.debug("could not get vehicles", err)
          this.vehicles = {}
          res(true)
        })
      })
    },

    getPresence(orgId) {return new Promise(res => {
        GetOrgVehiclePresence(orgId).then(result => {
          this.presences = result
          res(true)
        }).catch(err => {
          logger.debug("could not get vehicles", err)
          this.presences = []
          res(true)
        })
      })
    },

    async updateWithOrg (val) {
      this.loading++

      // if no org was provided
      if(!val) {
        return 
      }

      // this is a mutation in the pres store, it will update the devices for this org
      this.selectedFilter = []

      await Promise.all([this.getPresence(val), this.getVehicles(val)])

      this.tableData = await this.formatData(this.presences)

      this.loading--
    },

    formatData (presArray) {
      return new Promise(async res => {
        try {
          let data = presArray.map((item) => {
            return {
              date: moment(item.last_seen_local_time).format('YYYY DD MM'),
              excel_time: moment(item.last_seen_local_time).format('hh:mm:ss'),
              lat: getCoor(item.last_location.coords.lat),
              lng: getCoor(item.last_location.coords.long),
              status: item.status,
              vehicle: this.vehicles[item.vehicle_id], // find the vehicle name from our map
              device_id: item.device_id,
              time: moment(item.last_seen_local_time).format('YYYY MMM DD HH:mm:ss'),
              speed: item.last_location.speed === 'unknown' ? 'Unknown' : Math.round(item.last_location.speed || 0),
              mileage: Math.round((item.mileage || 0) / 1000),
              ignition: item.acc === '1' ? 'ON' : 'OFF',
              location: 'Not Known',
              actions: '',
              key: item.vehicle_id
            }
          })

          data = await this.findLocations(data)
          res(data)
        } catch (err) {
          logger.debug("data format error", err)
          res([])
        }
      })
    },

    download () {
      logger.debug("file download")
      
      if (this.tableData.length > 0) {
        // create our excel file
        const wb = xlsx.utils.book_new()
        
        // create our worksheets
        const report_data = this.tableData.map(i => {
          let entry = {}
          // we skip the last header as it's actions
          for(let x = 0; x < this.headers.length - 1; x++) {
            entry[this.headers[x].text] = i[this.headers[x].value]
          }
         return entry 
        })

        const report = xlsx.utils.json_to_sheet(report_data)
        const reportName = `${this.orgName}_Fleet_Status_Report_${moment().format("YYYY-MM-DD HH:mm:ss")}`

        // apend them to the workbook
        xlsx.utils.book_append_sheet(wb, report, this.reportTitle)

        xlsx.writeFile(wb, reportName + ".xlsx")
      }
    }
  }
}
</script>